import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { catchError, mapTo, of } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../services/auth.service';

export interface ChatRequest {
  mensagem: string;
  sessionId?: string | null;
}

export interface ChatResponse {
  resposta: string;
  sessionId?: string;
  arquivoGerado?: ChatGeneratedFile;
}

export interface ChatGeneratedFile {
  nomeArquivo: string;
  mimeType: string;
  conteudoBase64: string;
}

interface ApiGeneratedFile {
  nomeArquivo?: string;
  mimeType?: string;
  conteudoBase64?: string;
}

interface GeminiApiResponse {
  resposta?: string;
  sucesso?: boolean;
  erroMensagem?: string;
  sessionId?: string;
  SessionId?: string;
  sessionID?: string;
  sessionid?: string;
  session_id?: string;
  response?: string;
  answer?: string;
  mensagem?: string;
  text?: string;
  content?: string;
  arquivoGerado?: ApiGeneratedFile;
  nomeArquivo?: string;
  mimeType?: string;
  conteudoBase64?: string;
}

@Injectable({ providedIn: 'root' })
export class InteligenciaArtificialService {
  private apiUrl = `${environment.cedaeapp}/gemini/chat`;
  private clearSessionUrl = `${environment.cedaeapp}/gemini/chat/clear-session`;

  constructor(private http: HttpClient, private authService: AuthService) {}

  enviarMensagem(payload: ChatRequest): Observable<ChatResponse> {
    const body: Record<string, unknown> = { pergunta: payload.mensagem };
    if (payload.sessionId) {
      body['sessionId'] = payload.sessionId;
    }

    return this.http.post<unknown>(this.apiUrl, body, { headers: this.getHeaders() }).pipe(
      map((response) => this.extractResponse(response)),
    );
  }

  limparSessao(sessionId: string): Observable<void> {
    const normalized = (sessionId || '').trim();
    if (!normalized) {
      return of(void 0);
    }

    return this.http
      .post<unknown>(this.clearSessionUrl, { sessionId: normalized }, { headers: this.getHeaders() })
      .pipe(
        mapTo(void 0),
        catchError(() => of(void 0)),
      );
  }

  private getHeaders(): HttpHeaders {
    const token = this.authService.getToken() || localStorage.getItem('token') || '';
    return new HttpHeaders({
      'Content-Type': 'application/json-patch+json',
      Authorization: token ? `Bearer ${token}` : '',
    });
  }

  private extractResponse(response: unknown): ChatResponse {
    if (typeof response === 'string') {
      return { resposta: response };
    }

    if (response && typeof response === 'object') {
      const data = response as GeminiApiResponse;
      const sessionId = this.extractSessionId(data);
      const arquivoGerado = this.extractGeneratedFile(data);

      if (data.sucesso === false) {
        const errorText = (data.erroMensagem || data.resposta || '').trim();
        return { resposta: this.getFriendlyErrorMessage(errorText), sessionId, arquivoGerado };
      }

      const candidate =
        data.resposta ??
        data.response ??
        data.answer ??
        data.mensagem ??
        data.text ??
        data.content;

      if (typeof candidate === 'string' && candidate.trim()) {
        return { resposta: candidate, sessionId, arquivoGerado };
      }

      return {
        resposta: 'Recebi a resposta da IA, mas não consegui interpretar o formato retornado pelo servidor.',
        sessionId,
        arquivoGerado,
      };
    }

    return { resposta: 'Recebi a resposta da IA, mas não consegui interpretar o formato retornado pelo servidor.' };
  }

  private extractGeneratedFile(data: GeminiApiResponse): ChatGeneratedFile | undefined {
    const file = data.arquivoGerado ?? {};

    const nomeArquivo = (file.nomeArquivo ?? data.nomeArquivo ?? '').trim();
    const mimeType = (file.mimeType ?? data.mimeType ?? '').trim();
    const conteudoBase64 = (file.conteudoBase64 ?? data.conteudoBase64 ?? '').trim();

    if (!nomeArquivo || !mimeType || !conteudoBase64) {
      return undefined;
    }

    return { nomeArquivo, mimeType, conteudoBase64 };
  }

  private extractSessionId(data: GeminiApiResponse): string | undefined {
    const candidate =
      data.sessionId ??
      data.SessionId ??
      data.sessionID ??
      data.sessionid ??
      data.session_id;
    if (typeof candidate !== 'string') {
      return undefined;
    }

    const normalized = candidate.trim();
    return normalized || undefined;
  }

  private getFriendlyErrorMessage(rawError: string): string {
    const text = rawError.toLowerCase();

    if (text.includes('limite') || text.includes('rate') || text.includes('too many')) {
      const secondsMatch = rawError.match(/(\d+)\s*segundos?/i);
      const waitTime = secondsMatch?.[1];

      if (waitTime) {
        return `A IA esta com muitas consultas no momento. Tente novamente em ${waitTime} segundos.`;
      }

      return 'A IA esta com muitas consultas no momento. Tente novamente em instantes.';
    }

    return 'Ocorreu um erro ao consultar a IA. Tente novamente em alguns instantes.';
  }
}
